import React from 'react';
import {
  Box, Typography, Paper, Table, TableHead, TableRow, TableCell, TableBody,
  IconButton, Chip, Alert, Tooltip
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import RefreshIcon from '@mui/icons-material/Refresh';
import swal from 'sweetalert';
import Colors from '../../../components/ui/Colors';
import CustomLoader from '../../../components/CustomLoader';
import { listInjectedTables, retireInjectedTable } from '../../../utilityFunctions/AdminUtil';

const formatDate = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
};

export default function InjectedTablesList(props) {
  const { jwtToken, refreshKey } = props;

  const [rows, setRows] = React.useState([]);
  const [loading, setLoading] = React.useState(false);

  const load = React.useCallback(async () => {
    setLoading(true);
    const data = await listInjectedTables(jwtToken);
    setLoading(false);
    setRows(Array.isArray(data) ? data : []);
  }, [jwtToken]);

  React.useEffect(() => { load(); }, [load, refreshKey]);

  const handleRetire = async (tableName) => {
    const confirmed = await swal({
      title: `Retire ${tableName}?`,
      text: "This drops the Delta table and removes it from the agentic pipeline. This cannot be undone.",
      icon: "warning",
      buttons: ["Cancel", "Retire"],
      dangerMode: true,
    });
    if (!confirmed) return;

    setLoading(true);
    const result = await retireInjectedTable(tableName, jwtToken);
    setLoading(false);

    if (!result.ok) {
      swal({ title: "Error", text: result.error || "Retire failed", icon: "error" });
      return;
    }
    await load();
  };

  return (
    <Paper variant="outlined" sx={{ p: { xs: 1.5, md: 2 }, borderRadius: 2, overflow: 'hidden' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
        <Typography variant="h6" sx={{ color: Colors.primary, fontWeight: 700 }}>
          Active injected tables
        </Typography>
        <Tooltip title="Reload">
          <span>
            <IconButton onClick={load} disabled={loading} sx={{ color: Colors.primary }}><RefreshIcon /></IconButton>
          </span>
        </Tooltip>
      </Box>

      {loading && <CustomLoader />}

      {!loading && rows.length === 0 && (
        <Alert severity="info">No injected tables yet. Upload a spreadsheet above to add one.</Alert>
      )}

      {!loading && rows.length > 0 && (
        <Box sx={{ width: '100%', overflowX: 'auto' }}>
          <Table
            size="small"
            sx={{
              minWidth: 760,
              '& th': { fontWeight: 700, color: 'text.secondary', whiteSpace: 'nowrap' },
              '& td, & th': { borderColor: (theme) => theme.palette.divider },
              '& tbody tr:hover': { backgroundColor: alpha(Colors.primary, 0.04) },
            }}
          >
            <TableHead>
              <TableRow>
                <TableCell>Table</TableCell>
                <TableCell>Fully qualified name</TableCell>
                <TableCell>Created by</TableCell>
                <TableCell>Created</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((r) => (
                <TableRow key={r.id}>
                  <TableCell sx={{ fontFamily: 'monospace', fontWeight: 600, whiteSpace: 'nowrap' }}>{r.tableName}</TableCell>
                  <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.78rem', color: 'text.secondary', whiteSpace: 'nowrap' }}>{r.fullyQualifiedName}</TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>{r.createdBy || '—'}</TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatDate(r.createdAt)}</TableCell>
                  <TableCell>
                    <Chip size="small"
                      label={r.status}
                      color={r.status === 'ACTIVE' ? 'success' : 'default'} />
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="Retire (drop Delta table & remove from pipeline)">
                      <span>
                        <IconButton size="small" onClick={() => handleRetire(r.tableName)} disabled={r.status !== 'ACTIVE'}>
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      )}
    </Paper>
  );
}
